import { XIcon } from 'lucide-react';
import * as React from 'react';

import {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupInput,
  InputGroupText,
  InputGroupTextarea,
} from './components/ui/input-group.js';
import { clearNativeInput } from './lib/clear-native-input.js';
import { cn } from './lib/utils.js';

export function DimeInputGroup({
  className,
  ...props
}: Readonly<React.ComponentProps<typeof InputGroup>>) {
  return (
    <InputGroup
      // An autofilled field takes Chrome's autofill colour across the whole group, inside its border, so it looks like an autofilled plain input.
      className={cn(
        'has-[:autofill]:bg-[rgb(232,240,254)] has-[:autofill]:bg-clip-padding dark:has-[:autofill]:bg-[rgba(70,90,126,0.4)]',
        className,
      )}
      {...props}
    />
  );
}

export function DimeInputGroupAddon({
  ...props
}: Readonly<React.ComponentProps<typeof InputGroupAddon>>) {
  return <InputGroupAddon {...props} />;
}

export function DimeInputGroupButton({
  ...props
}: Readonly<React.ComponentProps<typeof InputGroupButton>>) {
  return <InputGroupButton {...props} />;
}

export function DimeInputGroupText({
  ...props
}: Readonly<React.ComponentProps<typeof InputGroupText>>) {
  return <InputGroupText {...props} />;
}

type DimeInputGroupInputProps = Readonly<
  React.ComponentProps<typeof InputGroupInput> & {
    /** Adds an X after the field that empties it while it has a value; off by default, since many groups already carry their own controls. */
    showClear?: boolean;
  }
>;

export function DimeInputGroupInput({
  showClear = false,
  className,
  ...props
}: DimeInputGroupInputProps) {
  const input = (
    <InputGroupInput
      // Chrome's own fill would stop short of the group's border, so it's held off for good and the group shows the colour instead.
      className={cn(
        'autofill:transition-[background-color] autofill:duration-[2147483647ms] autofill:[-webkit-text-fill-color:var(--foreground)]',
        showClear && 'peer',
        className,
      )}
      {...props}
      // WHY: `:placeholder-shown` is how the X knows the field is empty, and it needs a placeholder to match.
      placeholder={showClear ? (props.placeholder ?? ' ') : props.placeholder}
    />
  );
  if (!showClear) return input;

  return (
    <>
      {input}
      <InputGroupAddon
        align="inline-end"
        className="invisible pr-1 group-hover/input-group:visible peer-focus:visible peer-placeholder-shown:hidden peer-disabled:hidden peer-read-only:hidden"
      >
        <InputGroupButton
          size="icon-xs"
          tabIndex={-1}
          aria-hidden
          onMouseDown={(event) => event.preventDefault()}
          // The X's addon always renders straight after its input.
          onClick={(event) =>
            clearNativeInput(
              event.currentTarget.parentElement?.previousElementSibling as HTMLInputElement,
            )
          }
        >
          <XIcon />
        </InputGroupButton>
      </InputGroupAddon>
    </>
  );
}

export function DimeInputGroupTextarea({
  ...props
}: Readonly<React.ComponentProps<typeof InputGroupTextarea>>) {
  return <InputGroupTextarea {...props} />;
}
